import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as propertiesService from '@/services/properties.service';
import type { PropertyFilters } from '@/services/properties.service';
import type { Database } from '@/types/database';

type PropertyInsert = Database['public']['Tables']['properties']['Insert'];
type PropertyUpdate = Database['public']['Tables']['properties']['Update'];

// Query keys
export const propertyKeys = {
  all: ['properties'] as const,
  lists: () => [...propertyKeys.all, 'list'] as const,
  list: (filters?: PropertyFilters, page?: number, pageSize?: number) =>
    [...propertyKeys.lists(), { filters, page, pageSize }] as const,
  details: () => [...propertyKeys.all, 'detail'] as const,
  detail: (id: string) => [...propertyKeys.details(), id] as const,
};

// Hooks
export function useProperties(filters?: PropertyFilters, page = 1, pageSize = 50) {
  return useQuery({
    queryKey: propertyKeys.list(filters, page, pageSize),
    queryFn: () => propertiesService.getProperties(filters, page, pageSize),
  });
}

export function useProperty(id: string | undefined) {
  return useQuery({
    queryKey: propertyKeys.detail(id!),
    queryFn: () => propertiesService.getPropertyById(id!),
    enabled: !!id,
  });
}

export function useCreateProperty() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ property, createdBy }: { property: PropertyInsert; createdBy?: string }) => 
      propertiesService.createProperty(property, createdBy),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: propertyKeys.lists() });
    },
  });
}

export function useUpdateProperty() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: PropertyUpdate }) =>
      propertiesService.updateProperty(id, updates),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: propertyKeys.lists() });
      queryClient.invalidateQueries({ queryKey: propertyKeys.detail(data.id) });
    },
  });
}
